<template>
  <div>
    <div>
      <p class="title cell-item">
        <el-icon class="mr-2"><Edit /></el-icon>Order Management
      </p>
    </div>
    <div>
      <el-table
        :data="orderDevicesData"
        class="table background-container mb-0"
        row-key="id"
        :expand-row-keys="expandedRows"
        @expand-change="addExpandedRow"
      >
        <!-- Expand Column -->
        <el-table-column type="expand">
          <template #default="props">
            <div class="notes-section background-container">
              <el-timeline style="max-width: 600px">
                <el-timeline-item
                  v-for="note in props.row.notes"
                  :key="note.timestamp"
                  :timestamp="note.timestamp.toDate().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })"
                >
                  <div class="note-meta">
                    <span class="note-user">{{ note.user }}</span>
                  </div>
                  {{ note.content }}
                </el-timeline-item>
              </el-timeline>
              <el-input
                @update:model-value="value => newNotes[props.$index].content = value"
                :model-value="newNotes[props.$index].content"
                type="textarea"
                placeholder="Add a note"
              ></el-input>
              <span v-if="newNotes[props.$index].invalid" class="error-text"
                >Content cannot be empty</span
              >
              <div>
                <el-button class="mt-3" @click.prevent="addNotes(props.$index)"
                  >Save</el-button
                >
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column type="index" width="100" />
        <el-table-column
          v-for="column in tableColumns"
          :key="column.property"
          :label="column.label"
          :prop="column.property"
        >
          <template #default="{ $index, row }">
            <div v-if="editingIndex !== $index">
              <div v-if="column.input === 'date'">
                {{ row[column.property] ? row[column.property].toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : '' }}
              </div>
              <div v-else>
                {{ row[column.property] }}
              </div>
            </div>
            <div v-else>
              <div
                v-if="column.property === 'qaSignature' && row[column.property]"
              >
                {{ row[column.property] }}
              </div>
              <div
                v-else-if="column.property === 'qaDate' && row[column.property]"
              >
                {{ row[column.property].toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) }}
              </div>
              <div v-else>
                <el-input
                  @update:model-value="value => editingRow[column.property] = value"
                  :model-value="editingRow[column.property]"
                  v-if="column.input === 'string'"
                />
                <el-select
                  @update:model-value="value => editingRow[column.property] = value"
                  :model-value="editingRow[column.property]"
                  class="order-device-input"
                  v-else-if="column.input === 'selection'"
                >
                  <el-option
                    v-for="item in column.options"
                    :key="item"
                    :label="item"
                    :value="item"
                  />
                </el-select>
                <div v-else class="order-device-input">
                  <VueDatePicker
                    @update:model-value="value => editingRow[column.property] = value"
                    :model-value="editingRow[column.property]"
                    :min-date="minDate"
                    :teleport="true"
                    :dark="isDarkMode"
                  ></VueDatePicker>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Actions">
          <template #default="{ $index, row }">
            <button
              v-if="editingIndex !== $index"
              @click="handleEdit($index, row)"
              class="operations-btn"
            >
              <i class="ri-pencil-fill medium-icon"></i>
            </button>
            <span v-else
              ><button
                @click="updateRow(row, $index)"
                class="operations-btn mr-2"
              >
                <i class="ri-check-fill medium-icon"></i>
              </button>
              <button @click="cancelEdit" class="operations-btn close-btn">
                <i class="ri-close-fill medium-icon"></i>
              </button>
            </span>
            <el-popover
              ref="newOrderStatusPopover"
              placement="bottom"
              :width="350"
              trigger="click"
            >
              <template #reference>
                <button class="ml-2" v-if="orderDevicesData[$index].assignedTo">
                  <span
                    class="avatar"
                    >{{ getInitials(orderDevicesData[$index].assignedTo) }}</span
                  >
                </button>

                <button v-else class="operations-btn ml-2">
                  <i class="ri-user-fill"></i>
                </button>
              </template>
              <template #default>
                <el-select
                  :model-value="orderDevicesData[$index].assignedTo"
                  @update:model-value="updateAssignment($event, $index)"
                  placeholder="Assign to"
                  style="width: 100% !important"
                >
                  <el-option
                    class="assign-to-select"
                    v-for="item in ['Arvin Tashakori', 'Megan Ma']"
                    :key="item"
                    :label="item"
                    :value="item"
                  >
                    <span class="avatar">{{ getInitials(item) }}</span>
                    <span>{{ item }}</span>
                  </el-option>
                </el-select>
              </template>
            </el-popover>

          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { Timestamp, arrayUnion } from 'firebase/firestore'
import { updateDocument } from '../../../services/useDocument'
import firebase from '../../../../firebase'
import { useStore } from 'vuex'

export default {
  name: 'PreorderForm',
  props: ['preorder'],
  components: {
    VueDatePicker
  },
  setup (props, content) {
    const orderDevicesData = ref([])
    const editingIndex = ref(-1)
    const editingRow = ref(null)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const minDate = ref(today)
    const { handlelUpdate } = updateDocument('kneeSleevePreorders', props.preorder.id)
    const newNotes = ref([])
    const expandedRows = ref([])
    const store = useStore()
    const isDarkMode = ref(false)
    const deviceDialogOpen = ref(false)

    const openDeviceDialog = () => {
      deviceDialogOpen.value = true
    }

    const closeDeviceDialog = () => {
      deviceDialogOpen.value = false
    }

    watch(() => store.state.theme.currentTheme, (newTheme) => {
      isDarkMode.value = newTheme === 'night'
    }, { immediate: true })

    const tableColumns = [
      { label: 'Board ID', property: 'boardId', input: 'string' },
      { label: 'Firmware Version', property: 'firmwareVersion', input: 'string' },
      { label: 'Status', property: 'status', input: 'selection', options: ['Pending', 'In Assembly', 'Quality Check Completed', 'Packaging', 'Shipped'] },
      { label: 'QA Signature', property: 'qaSignature', input: 'string' },
      { label: 'QA Date', property: 'qaDate', input: 'date' }
    ]

    // Function to generate default data for one device
    const generateDeviceData = (i) => {
      return {
        boardId: '',
        firmwareVersion: '',
        status: '',
        qaSignature: '',
        qaDate: null,
        notes: [],
        id: i,
        assignedTo: ''
      }
    }

    onMounted(() => {
      newNotes.value = Array.from({ length: props.preorder.quantity }, (_, i) => ({
        content: '',
        timestamp: null,
        invalid: false,
        user: firebase.firebaseAuth.currentUser.email
      }))
    })

    watch(() => props.preorder, (newVal) => {
      const newData = []

      for (let i = 0; i < newVal.quantity; i++) {
        if (newVal.orderDevicesData && newVal.orderDevicesData[i]) {
          const deviceData = { ...newVal.orderDevicesData[i] }
          deviceData.id = i
          if (deviceData.qaDate) {
            deviceData.qaDate = deviceData.qaDate.toDate()
          }

          newData.push(deviceData)
        } else {
          newData.push(generateDeviceData(i))
        }
      }

      orderDevicesData.value = newData
    }, { immediate: true })

    const handleEdit = (index, row) => {
      editingIndex.value = index
      editingRow.value = { ...row }
    }

    const updateRow = (row, index) => {
      if (editingRow.value.qaDate) {
        editingRow.value.qaDate = Timestamp.fromDate(editingRow.value.qaDate)
      }
      if (orderDevicesData.value[index].notes) {
        editingRow.value.notes = orderDevicesData.value[index].notes
      }
      const updatePath = `orderDevicesData.${index}`

      handlelUpdate({ [updatePath]: editingRow.value })

      editingIndex.value = -1
      editingRow.value = null
    }

    const updateAssignment = (name, index) => {
      const updatePath = `orderDevicesData.${index}.assignedTo`
      handlelUpdate({ [updatePath]: name })
    }

    const cancelEdit = () => {
      editingIndex.value = -1
      editingRow.value = null
    }

    const addNotes = (index) => {
      if (!newNotes.value[index].content) {
        newNotes.value[index].invalid = true
        return
      }
      delete newNotes.value[index].invalid
      newNotes.value[index].timestamp = Timestamp.now()
      const dataExist = props.preorder.orderDevicesData && props.preorder.orderDevicesData[index]
      const updatePath = `orderDevicesData.${index}`
      const updatePayload = dataExist
        ? { [`${updatePath}.notes`]: arrayUnion(newNotes.value[index]) }
        : { [updatePath]: { notes: [newNotes.value[index]], id: index } }

      handlelUpdate(updatePayload)
      newNotes.value[index].content = ''
      newNotes.value[index].invalid = false
    }

    const addExpandedRow = (row, expanded) => {
      if (expandedRows.value.includes(row.id)) {
        expandedRows.value = expandedRows.value.filter((id) => id !== row.id)
      } else {
        expandedRows.value = [...expandedRows.value, row.id]
      }
    }

    const getInitials = (name) => {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase()
    }
    return {
      orderDevicesData,
      tableColumns,
      editingIndex,
      handleEdit,
      updateRow,
      cancelEdit,
      minDate,
      editingRow,
      addNotes,
      newNotes,
      expandedRows,
      addExpandedRow,
      isDarkMode,
      updateAssignment,
      getInitials,
      deviceDialogOpen,
      openDeviceDialog,
      closeDeviceDialog
    }
  }
}
</script>

<style scoped>
.cell-item {
  display: flex;
  align-items: center;
  background-color: var(--lavendar) !important;
}

.title {
  background: var(--light-grey);
  border-top: 1px solid var(--border-color);
  padding: 13px 15px;
  font-size: 14px;
  margin-bottom: 0px !important;
  border-bottom: 1px solid var(--border-color);
}

.operations-btn {
  background-color: var(--dark-grey);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  padding: 2px 7px;
  margin: 0;
  font-size: 14px;
  color: var(--text-color);
}

.operations-btn.close-btn {
  background-color: var(--error-color);
  color: var(--text-color);
}

.order-device-input {
  width: 95% !important;
}

.notes-section {
  padding: 10px 16px;
  margin: 10px 0;
}

.avatar {
  display: inline-block;
  background-color: var(--accent-color); /* Avatar background color */
  color: white; /* Avatar text color */
  font-weight: bold;
  font-size: 12px; /* Decrease font size for more 'padding' */
  width: 28px; /* Increase width for proper spacing */
  height: 28px; /* Increase height for proper spacing */
  line-height: 28px; /* Adjust to center the text vertically, should be same as height */
  border-radius: 50%;
  text-align: center;
  margin-right: 8px; /* Space between the avatar and the name */
}
</style>

<style>
.error-text {
  color: var(--error-color);
  font-size: 12px;
}

.assign-to-select.el-select-dropdown__item {
  height: 44px !important;
  line-height: 44px !important;
}
</style>
